/**
 * Password strength indicator component
 */

import { useEffect, useState } from 'react'
import zxcvbn from 'zxcvbn'
import { cn } from '@/lib/utils/cn'

interface PasswordStrengthProps {
  password: string
  showLabel?: boolean
}

const strengthLabels = ['Very Weak', 'Weak', 'Fair', 'Strong', 'Very Strong']
const strengthColors = [
  'bg-destructive',
  'bg-orange-500',
  'bg-yellow-500',
  'bg-blue-500',
  'bg-green-500',
]

export function PasswordStrength({ password, showLabel = true }: PasswordStrengthProps) {
  const [strength, setStrength] = useState(0)

  useEffect(() => {
    if (!password) {
      setStrength(0)
      return
    }

    const result = zxcvbn(password)
    setStrength(result.score)
  }, [password])

  if (!password) return null

  return (
    <div className="space-y-2">
      <div className="flex gap-1">
        {[0, 1, 2, 3, 4].map((level) => (
          <div
            key={level}
            className={cn(
              'h-2 flex-1 rounded-full transition-colors',
              level <= strength ? strengthColors[strength] : 'bg-muted'
            )}
          />
        ))}
      </div>
      {showLabel && (
        <p className="text-xs text-muted-foreground">
          Password strength: <span className="font-medium">{strengthLabels[strength]}</span>
        </p>
      )}
    </div>
  )
}
